import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import SessionEmpty from "@images/empty/session_empty.svg"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramFilter } from "@modules/programs/types/programTypes"
import { type DetailedSessionType } from "@modules/sessions/sessionsType"
import { TrainingCalendar } from "@modules/training/components/trainingCalendar"
import { TrainingHeader } from "@modules/training/components/trainingHeader"
import { usePlanningSessionEvents } from "@modules/training/hooks/useTraining"
import { EmptyView } from "@modules/ui/components/emptyView"
import { Plus, Search } from "lucide-react"
import { useState } from "react"

type TrainingContentProps = {
  calendarRange?: { startDate: string; endDate: string }
  className?: string
} & React.ComponentProps<"div">

export function TrainingContent({ calendarRange, className, ...props }: TrainingContentProps) {
  const [openedSession, setOpenedSession] = useState<DetailedSessionType>()

  // Fetch owned and subscribed programs using the correct usePrograms API with destructuring
  const {
    data: { content: ownedPrograms },
    isLoading: isLoadingOwnedPrograms,
  } = usePrograms({ programFilter: ProgramFilter.OWNED })
  const {
    data: { content: subscribedPrograms },
    isLoading: isLoadingSubscribedPrograms,
  } = usePrograms({ programFilter: ProgramFilter.SUBSCRIBED })

  const { refreshCalendarEvents } = useCalendar()
  const { data: sessions, isLoading: isLoadingSessions } = usePlanningSessionEvents(
    calendarRange?.startDate,
    calendarRange?.endDate,
  )

  const { navigateToProgramCreationTypeSelection } = useProgramSidebar()
  const { navigateToSessionView } = useSessionSidebar()
  const mySpaceNavigate = useMySpaceNavigate()

  const showEmptyView =
    !isLoadingOwnedPrograms &&
    !isLoadingSubscribedPrograms &&
    !(ownedPrograms.length > 0 || subscribedPrograms.length > 0)

  const onClickOnSlot = (slot: DetailedSessionType) => {
    navigateToSessionView(slot, refreshCalendarEvents, () => {
      setOpenedSession(undefined)
      return Promise.resolve(true)
    })
    setOpenedSession(slot)
  }

  const onCreateProgramClick = () => {
    navigateToProgramCreationTypeSelection()
  }

  const onDiscoverClick = () => {
    mySpaceNavigate(Path.MySpace.Explore)
  }

  return (
    <div
      className={cn(
        "flex h-full w-full min-w-245 flex-col gap-2 px-6 py-6 min-[1961px]:w-426 min-[1961px]:px-0 lg:mx-auto",
        className,
      )}
      {...props}
    >
      <TrainingHeader />

      {showEmptyView ? (
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center justify-center rounded-4xl bg-surface-foreground p-5">
            <EmptyView
              image={{ src: SessionEmpty, padding: "0" }}
              title={word("planning.empty.title")}
              description={word("planning.empty.description")}
              flexGrow={false}
              className="bg-surface-background p-8"
              disposition="row"
              paddingType="In_table"
              imagePosition="after"
              textAlign="left"
            >
              <div className="flex flex-row items-center gap-3">
                <Button variant="primary" onClick={onDiscoverClick}>
                  <Search />

                  {word("programs.discover")}
                </Button>
                <Text fontStyle="LL">{word("global.or")}</Text>
                <Button variant="outline" onClick={onCreateProgramClick}>
                  <Plus />

                  {word("program.form.addProgram.title")}
                </Button>
              </div>
            </EmptyView>
          </div>
        </div>
      ) : (
        <TrainingCalendar
          sessions={sessions}
          onClickOnSlot={onClickOnSlot}
          detailedSessionId={openedSession?.id}
          isLoadingSessions={isLoadingSessions}
        />
      )}
    </div>
  )
}
